import {defineStore} from "pinia";
import {DefaultPaginationItems, defaultTake, type PaginationItems} from "@/models/pagination";
import type {
    AssignmentListModel,
    AssignmentListRequest
} from "@/models/assignment/assignment-list";
import {
    cancelAssignment,
    removeAssignmentUrgency,
    createAssignment,
    downloadAssignmentWayBill,
    getAvailableAssignmentTransportReasons,
    getAssignmentRouting,
    getAssignment,
    getAssignments,
    getAssignmentWayBillsWithDamageImages,
    getAssignmentOverlaps,
    switchAssignmentInStorageStatus,
    getAmountReceivedAssignments,
    getAmountReadyForPromatie
} from "@/services/assignment/assignment-service";
import {
    DefaultAssignmentCreateData,
    DefaultAssignmentCreateModel,
    type AssignmentDetailData,
    type AssignmentDetailModel
} from "@/models/assignment/assignment-detail";
import {type RemovableRef, useStorage} from "@vueuse/core";
import {DefaultAssignment, type Assignment} from "@/models/assignment/assignment";
import {resolveAssignmentWindowEnvelope} from "@/helpers/assignment-window-helpers";
import type {Vehicle} from "@/models/vehicle/vehicle";
import {
    type AssignmentRoutingModel,
    AssignmentRoutingModelDefaults
} from "@/models/assignment/routing";
import type {AssignmentReason} from "@/models/assignment/assignment-reason";
import type { WayBillsWithDamageImages } from "@/models/assignment/way-bills";
import { DefaultFilterRequest } from "@/models/filter";
import { populateWayBillDamageReportImageBase64 } from "@/helpers/damage-report-image-helper";
import type { AssignmentOverlapInfo, AssignmentOverlapRequest, AssignmentOverlapVehicleRequest } from "@/models/assignment/assignment-overlap";
import { useAuthStore } from "@/stores/authentication-store";
import { Role } from "@/constants/role";

export class AssignmentRoutes {
    static TrajectoriesTab = "trajectories";
    static StatusHistoryTab = "statusHistory";
    static WayBillsTab = "wayBills";
}

interface AssignmentState {
    loading: boolean;
    loadingRouting: boolean;
    loadingWayBills: boolean;
    editMode: boolean;
    assignments: PaginationItems<AssignmentListModel>;
    assignmentListRequest: AssignmentListRequest;
    createAssignmentRequest: AssignmentDetailModel;
    createAssignmentData: AssignmentDetailData;
    availableTransportReasons: AssignmentReason[];
    selectedAssignment: Assignment;
    selectedAssignments: AssignmentListModel[];
    totalAmountReceivedAssignments: number;
    totalAmountReadyForPromatieAssignments: number;
    routing: AssignmentRoutingModel,
    activeRoutingTab: RemovableRef<string>;
    expandedDestinations: string[];
    wayBillsWithDamageImages: WayBillsWithDamageImages[];
    overlapValidationDetails: AssignmentOverlapInfo[];
}

export const useAssignmentStore = defineStore({
    id: 'assignment',
    state: () => ({
        loading: false,
        loadingRouting: false,
        loadingWayBills: false,
        editMode: false,
        assignmentListRequest: {
            filterOptions: DefaultFilterRequest(),
            page: 1,
            take: defaultTake,
            orderBy: "transportDate",
            isDescending: false,
        },
        assignments: DefaultPaginationItems(),
        selectedAssignment: DefaultAssignment(),
        selectedAssignments: [],
        totalAmountReceivedAssignments: 0,
        totalAmountReadyForPromatieAssignments: 0,
        createAssignmentRequest: DefaultAssignmentCreateModel(),
        availableTransportReasons: [],
        routing: AssignmentRoutingModelDefaults(),
        activeRoutingTab: useStorage("assignment.active-routing-tab", AssignmentRoutes.TrajectoriesTab),
        expandedDestinations: [],
        createAssignmentData: DefaultAssignmentCreateData(),
        wayBillsWithDamageImages: [],
        overlapValidationDetails: [],
    } as AssignmentState),
    getters: {
        hasOverlapValidationErrors: (state) => state.overlapValidationDetails.length > 0,
    },
    actions: {
        async getAssignments() {
            try {
                this.loading = true;
                this.assignments = DefaultPaginationItems();

                const response = await getAssignments(this.assignmentListRequest)
                if (response.status === 200) {
                    this.assignments = response.data;
                }
                
                const totalAmountReceivedAssignments = await getAmountReceivedAssignments();
                this.totalAmountReceivedAssignments = totalAmountReceivedAssignments.data;

                const authStore = useAuthStore();
                const isOrderSubmitterRole = authStore.role === Role.Client || authStore.role === Role.ExternalUser;
                if (!isOrderSubmitterRole) {
                    const totalAmountReadyForPromatieAssignments = await getAmountReadyForPromatie();
                    this.totalAmountReadyForPromatieAssignments = totalAmountReadyForPromatieAssignments.data;
                }
            } finally {
                this.loading = false;
            }
        },
        async createAssignment() {
            try {
                this.loading = true;
                const response = await createAssignment(this.createAssignmentRequest);
                const errorData = (
                    response as { response?: { data?: { errorData?: Record<string, unknown> } } }
                )?.response?.data?.errorData;

                const hasOverlapError = !!errorData && Object.values(errorData)
                    .flatMap((details) => Array.isArray(details) ? details : [])
                    .some((detail) => (detail as { errorMessage?: string })?.errorMessage === "ASSIGNMENT_VEHICLE_OVERLAP");
                if (hasOverlapError) {
                    await this.checkOverlapsForCreate();
                } else {
                    this.overlapValidationDetails = [];
                }
                if (response.status === 200) {
                    this.overlapValidationDetails = [];
                }
                return response;
            } finally {
                this.loading = false;
            }
        },
        async getAssignment(id: number) {
            try {
                this.loading = true;
                const response = await getAssignment(id)
                if (response.status === 200) {
                    this.selectedAssignment = response.data;
                }
            } finally {
                this.loading = false;
            }
        },
        async getRouting(id: number) {
            try {
                this.loadingRouting = true;
                const response = await getAssignmentRouting(id)
                if (response.status === 200) {
                    this.routing = response.data;
                }
            } finally {
                this.loadingRouting = false;
            }
        },
        async cancelAssignment(id: number) {
            try {
                this.loading = true;
                const response = await cancelAssignment(id);
                return response.status === 200;
            } finally {
                this.loading = false;
            }
        },
        async removeUrgency(id: number) {
            try {
                this.loading = true;
                return await removeAssignmentUrgency(id);
            } finally {
                this.loading = false;
            }
        },
        async switchInStorageStatus() {
            try {
                this.loading = true;
                return await switchAssignmentInStorageStatus(this.selectedAssignment.id);
            } finally {
                this.loading = false;
            }
        },
        removeVehicle(row: Vehicle) {
            this.createAssignmentRequest.vehicles = this.createAssignmentRequest.vehicles.filter(vehicle => vehicle.licensePlate !== row.licensePlate);
        },
        async getTransportReasons() {
            const response = await getAvailableAssignmentTransportReasons();
            if (response.status === 200) {
                this.availableTransportReasons = response.data;
            }
        },
        async downloadWayBill(wayBillId: number) {
            try {
                this.loading = true;
                const response = await downloadAssignmentWayBill(wayBillId);
                if (response.status === 200) {
                    return response.data;
                }
            } finally {
                this.loading = false;
            }
        },
        async getWayBillsWithDamageImages(id: number) {
            try {
                this.loadingWayBills = true;
                const response = await getAssignmentWayBillsWithDamageImages(id);
                if (response.status === 200) {
                    const wayBillsWithDamageImages = response.data.wayBillsWithDamageImages;
                    await populateWayBillDamageReportImageBase64(wayBillsWithDamageImages);
                    this.wayBillsWithDamageImages = wayBillsWithDamageImages;
                }
            } finally {
                this.loadingWayBills = false;
            }
        },
        async checkOverlapsForCreate() {
            const envelope = resolveAssignmentWindowEnvelope(
                this.createAssignmentRequest.pickUpDropOffDateTime,
                this.createAssignmentRequest.pickUpWindow,
                this.createAssignmentRequest.dropOffWindow
            );
            if (!envelope) {
                this.overlapValidationDetails = [];
                return;
            }

            if (envelope[1] < envelope[0]) {
                this.overlapValidationDetails = [];
                return;
            }

            if (this.createAssignmentRequest.vehicles.length === 0) {
                this.overlapValidationDetails = [];
                return;
            }

            const vehicles: AssignmentOverlapVehicleRequest[] = this.createAssignmentRequest.vehicles.map(vehicle => ({
                vehicleId: vehicle.id,
                licensePlate: vehicle.licensePlate,
                chassisNumber: vehicle.chassisNumber
            }));

            const request: AssignmentOverlapRequest = {
                pickUpStartDateTime: envelope[0],
                dropOffEndDateTime: envelope[1],
                vehicles: vehicles
            };

            const response = await getAssignmentOverlaps(request);

            if (response.status === 200) {
                this.overlapValidationDetails = response.data;
                return;
            }

            this.overlapValidationDetails = [];
        },
        clearOverlapValidationError() {
            this.overlapValidationDetails = [];
        },
    }
})

